import React, { useCallback } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Button, ButtonSize } from "@amzn/stencil-react-components/button";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ManageApplicationCardHeader } from "./card-header";
import { H1, H2 } from "../typography";
import { useHistory } from "react-router-dom";
import useArb from "../../hooks/use-arb";
import { DisplayNewUIComponent } from "../../utility/new-ui-toggle";
import { HVH_COLORS } from "../../config/palette";

export interface CardDetail {
  errorCode?: number;
  jobTitle: string;
  hours: string;
  startDate: string;
  startTime: string;
  description: string;
  payRate: string;
  address: string;
  image: string;
  isTentativeStartDate: boolean;
  signOnBonus: string;
  scheduleBannerText: string;
  hoursPerWeek: string;
  employmentType: string;
  employmentTypeL10N?: string;
  parsedTrainingDate: string;
  monthlyBasePayL10N: string;
  currencyCode: string;
  supportedLanguage?: string;
  isVirtual?: boolean;
}

interface ManageApplicationCardControlledProps {
  details?: CardDetail;
  loading?: boolean;
  error?: boolean;
  onRetry: () => void;
  eligibleForCSS: boolean;
  eligibleForWithdraw: boolean;
  scheduleIdFound: boolean;
  cssURL: string;
  dashboardURL: string;
  onChangeSchedule: () => void;
  onWithdrawApplication: () => void;
  onWithdrawApplicationConfirm: (withdrawReason: string) => void;
  applicationId: string;
  collapsed: boolean;
  eligibleForAppointmentRescheduling: boolean;
  appointmentSelfServiceURL: string;
}

export const ManageApplicationCardControlled = ({
  applicationId,
  details,
  onRetry,
  loading,
  error,
  eligibleForCSS,
  eligibleForWithdraw,
  scheduleIdFound,
  cssURL,
  dashboardURL,
  onChangeSchedule,
  onWithdrawApplication,
  onWithdrawApplicationConfirm,
  eligibleForAppointmentRescheduling,
  appointmentSelfServiceURL,
  collapsed,
}: ManageApplicationCardControlledProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  const history = useHistory();
  const navigateToCSSUrl = useCallback(async () => {
    await onChangeSchedule();
    cssURL.startsWith("http") ? window.location.assign(cssURL) : history.push(cssURL);
  }, [history]);

  const navigateToDashboardUrl = useCallback(async () => {
    dashboardURL.startsWith("http") ? window.location.assign(dashboardURL) : history.push(dashboardURL);
  }, [history]);

  const retry = bundle.getMessage("Checklist-General-RetryButtonText");
  const jobDetails = bundle.getMessage("Checklist-JobCard-JobDetails");
  const unableToRetrieve = bundle.getMessage("Checklist-JobDetails-Error");
  const loadingText = bundle.getMessage("checklist-General-TaskStatus-Loading");

  if (loading) {
    if (DisplayNewUIComponent) {
      return (
        <Col width={"100%"}>
          {!collapsed && (
            <Row padding={"1rem"} alignItems={"center"} justifyContent={"space-between"}>
              <H2>{jobDetails}</H2>
            </Row>
          )}

          <Col alignItems="center" padding={"2rem"} backgroundColor={HVH_COLORS.NEUTRAL_0}>
            <Spinner size={SpinnerSize.Small} loadingText={loadingText} showText={true} />
          </Col>
        </Col>
      );
    }
    return <Spinner size={SpinnerSize.Small} loadingText={loadingText} showText={true} />;
  }

  if (error) {
    return null; // Remove and customize the error state if needed
    return (
      <Col gridGap="1rem">
        <MessageBanner type={MessageBannerType.Error}>{unableToRetrieve}</MessageBanner>
        <Row>
          <Button size={ButtonSize.Small} onClick={onRetry}>
            {retry}
          </Button>
        </Row>
      </Col>
    );
  }

  if (!scheduleIdFound || !details) {
    return null;
  }

  const header = DisplayNewUIComponent ? <H2>{jobDetails}</H2> : <H1>{jobDetails}</H1>;

  return (
    <>
      <Col width="100%">
        {!collapsed && <Col padding={"1rem"}>{header}</Col>}
        <ManageApplicationCardHeader
          applicationId={applicationId}
          jobTitle={details.jobTitle}
          hours={details.hours}
          startDate={details.startDate}
          startTime={details.startTime}
          description={details.description}
          payRate={details.payRate}
          signOnBonus={details.signOnBonus}
          hoursPerWeek={details.hoursPerWeek}
          address={details.address}
          image={details.image}
          scheduleBannerText={details.scheduleBannerText}
          isTentativeStartDate={details.isTentativeStartDate}
          onChangeScheduleClick={navigateToCSSUrl}
          onWithdrawApplication={onWithdrawApplication}
          onWithdrawApplicationConfirm={onWithdrawApplicationConfirm}
          onGoToDashboardClick={navigateToDashboardUrl}
          eligibleForCSS={eligibleForCSS}
          eligibleForWithdraw={eligibleForWithdraw}
          supportedLanguage={details.supportedLanguage}
          employmentType={details.employmentType}
          employmentTypeL10N={details?.employmentTypeL10N || ""}
          parsedTrainingDate={details.parsedTrainingDate}
          monthlyBasePayL10N={details.monthlyBasePayL10N}
          currencyCode={details.currencyCode}
          isVirtual={details.isVirtual}
          collapsed={collapsed}
          eligibleForAppointmentRescheduling={eligibleForAppointmentRescheduling}
          appointmentSelfServiceURL={appointmentSelfServiceURL}
        />
      </Col>
    </>
  );
};
